import { NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { scrollToFirstInvalidControl } from '@common/scroll-to-first-invalid-control';
import { AddressInputsComponent } from '@components/address-inputs/address-inputs.component';
import { CardComponent } from '@components/card/card.component';
import { PaymentCardFormComponent } from '@components/consultation-request/payment-card-form/payment-card-form.component';
import { PaymentSubmitButtonComponent } from '@components/consultation-request/payment-submit-button/payment-submit-button.component';
import { AdditionalRecommendedTestingComponent } from '@components/order-address/additional-recommended-testing/additional-recommended-testing.component';
import { OrderPhoneInputsComponent } from '@components/order-phone-inputs/order-phone-inputs.component';
import { CustomerTestsTypes } from '@enums/customer-tests-types';
import { DataLayerCategories } from '@enums/data-layer-categories';
import { PaymentTypes as LegacyPaymentTypes } from '@enums/payment-types';
import { environment } from '@environments/environment';
import { IncompleteOrderFormError } from '@errors/incomplete-order-form-error';
import { InternationalCreditCardNotSupportedError } from '@errors/international-credit-card-not-supported-error';
import { MissingFormFieldsError } from '@errors/missing-form-fields-error';
import { ConfigService as PaymentConfigService, Payment } from '@medology/ngx-payment';
import { LegacyUpsellStoreResponse } from '@models/legacy-upsell-store-response.model';
import { OrderUpsell } from '@models/order-upsell';
import { ReorderAddress } from '@models/reorder-address';
import { AddressUpdateRequest } from '@models/requests/address-update.model';
import { LegacyUpsellStoreRequest } from '@models/requests/legacy-upsell-store.model';
import { Upsell } from '@models/upsell';
import { BusinessAccountService } from '@services/business-account.service';
import { DataLayerService } from '@services/data-layer.service';
import { ErrorHandlerService } from '@services/error-handler.service';
import { FormService } from '@services/form.service';
import { LoadingService } from '@services/loading.service';
import { NavigationService } from '@services/navigation.service';
import { OrderService } from '@services/order.service';
import { PaymentService, provideDefaultPaymentConfig } from '@services/payment.service';
import { StorageService } from '@services/storage.service';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-order-address',
  templateUrl: './order-address.component.html',
  styleUrls: ['./order-address.component.scss'],
  imports: [
    CardComponent,
    NgIf,
    OrderPhoneInputsComponent,
    AddressInputsComponent,
    FormsModule,
    ReactiveFormsModule,
    AdditionalRecommendedTestingComponent,
    PaymentCardFormComponent,
    PaymentSubmitButtonComponent,
  ],
  providers: [provideDefaultPaymentConfig()],
})
export class OrderAddressComponent implements OnInit, OnDestroy {
  @ViewChild('paymentCardFormComponent', { static: false }) public paymentCardFormComponent: PaymentCardFormComponent;

  public addressContactInformation: FormGroup;
  public upsellTestsForm: FormGroup;
  public tos: string;
  public domain = 'STDcheck';
  public upsellTests: OrderUpsell[];
  public processing: boolean = false;
  public upsellPaymentFailed: boolean = false;
  public submittedForm: boolean = false;
  public formValidationError: Error | null = null;
  public submissionErrors: string[] = [];
  public upsellSelected: OrderUpsell;
  public payments: Payment[] = [];

  private formValidationSubscription: Subscription | null = null;
  private isFormValidationActive: boolean = false;

  private formBuilder = inject(FormBuilder);
  private formService = inject(FormService);
  private orderService = inject(OrderService);
  private paymentService = inject(PaymentService);
  private dataLayerService = inject(DataLayerService);
  private storageService = inject(StorageService);
  private router = inject(Router);
  private loadingService = inject(LoadingService);
  private errorHandlerService = inject(ErrorHandlerService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private navigationService = inject(NavigationService);
  private businessAccountService = inject(BusinessAccountService);
  private paymentConfigService = inject(PaymentConfigService);
  private reorder: ReorderAddress;

  /**
   * Determines if the upsell payment form should be displayed.
   */
  public get shouldDisplayPaymentForm(): boolean {
    return Boolean(this.upsellPaymentFailed && this.upsellTestsForm && this.upsellSelected);
  }

  /**
   * Gets the address form.
   */
  public get addressForm(): FormGroup {
    return this.addressContactInformation.get('address') as FormGroup;
  }

  /**
   * It returns the terms of service page link based on the domain
   */
  public get tosLinkAddress(): string {
    return environment.siteUrls.termsOfService;
  }

  /**
   * Get if it is a phone order.
   */
  public get isPhoneOrder(): boolean {
    return this.storageService.orderType === 'phone';
  }

  /**
   * Get the checkout button text
   */
  public get checkoutButtonText(): string {
    if (this.shouldDisplayPaymentForm) {
      return environment.buttons.checkout;
    }

    return 'Finish Placing My Order';
  }

  /**
   * Return the class used in payment button wrapper
   */
  public get getPaymentButtonWrapperClass(): string {
    if (this.shouldDisplayPaymentForm) {
      return 'is-block my-4';
    }

    return 'is-block my-4 is-fullwidth is-success action is-large';
  }

  /**
   * Return the class used in submit button inside the payment button wrapper
   */
  public get getSubmitButtonClass(): string {
    if (this.shouldDisplayPaymentForm) {
      return 'has-background-orange';
    }

    return 'has-background-green';
  }

  /**
   * Initializes component features.
   */
  public ngOnInit(): void {
    this.paymentService.setGiftCardConfig(this.paymentConfigService);
    this.initAddressForm();
    this.initRequests();
    this.dataLayerService.handleDataLayer();
    this.domain = environment.domain;
  }

  /**
   * Run actions when component is destroyed.
   */
  public ngOnDestroy(): void {
    this.resetFormValidationSubscription();
  }

  /**
   * Submits the form.
   */
  public submit(): void {
    this.submittedForm = true;
    this.processing = true;
    this.resetErrorsMessages();
    this.addressContactInformation.markAllAsTouched();
    this.checkErrors();

    if (this.hasErrors()) {
      this.processing = false;

      return;
    }

    this.updatePatientPhoneInStorageIfNonEmpty();
    this.submitWorkflow().subscribe({
      next: () => this.continueOrderingWorkflow(),
      error: () => (this.processing = false),
    });
  }

  /**
   * Determines if the control is invalid and touched.
   *
   * @param {AbstractControl} control the control to be checked
   *
   * @returns true if the control is invalid and touched, false otherwise
   */
  public isInvalid(control: AbstractControl): boolean {
    return control?.invalid && control?.touched;
  }

  /**
   * Updates the form validation errors based on order address visible forms.
   */
  public checkFormValidationError(): void {
    const paymentFormValid = this.paymentCardFormComponent ? this.paymentCardFormComponent?.validateForm() : true;
    const paymentFormIncomplete =
      this.shouldDisplayPaymentForm && !this.payments.length && !this.paymentCardFormComponent?.isPayLater;

    if (this.addressContactInformation.invalid) {
      this.formValidationError = new MissingFormFieldsError();

      return;
    }

    if (!paymentFormValid || paymentFormIncomplete) {
      this.formValidationError = new IncompleteOrderFormError();

      return;
    }
  }

  /**
   * Handler for the StoreUpsell request when it succeeds.
   *
   * @param { LegacyUpsellStoreResponse } upsellStoreResponse the successful response object
   */
  public handleSuccessfulStoreUpsell(upsellStoreResponse: LegacyUpsellStoreResponse): void {
    this.storageService.paymentType = LegacyPaymentTypes.Recharge;
    this.orderService.addUpsellTestToStorage(this.upsellSelected);
    this.orderService.addUpsellPriceToTotal(this.upsellSelected.upsell_price);
    if (upsellStoreResponse.transaction_id) {
      this.storageService.upsell = [upsellStoreResponse.transaction_id];
    }

    this.dataLayerService.pushDataLayerTracking(DataLayerCategories.Upsell);
  }

  /**
   * Handle upsell selected event by setting it in the component.
   *
   * @param {OrderUpsell} upsell the selected upsell
   */
  public handleUpsellSelectedEvent(upsell: OrderUpsell): void {
    this.upsellSelected = upsell;

    if (!upsell && this.storageService.addressComplete) {
      this.payments = [];
      this.resetErrorsMessages();
    }

    this.changeDetectorRef.detectChanges();
  }

  /**
   * Continue the ordering workflow.
   */
  public continueOrderingWorkflow(): void {
    this.orderService.showOrderBetterLab().subscribe({
      next: (response) => {
        this.storageService.betterLab = !!response.has_better_lab;
        this.router.navigate([this.navigationService.getNextNavigationUrl()]);
      },
      error: () => this.router.navigate([this.navigationService.getNextNavigationUrl()]),
    });
  }

  /**
   * Handles payment selection from the payment form.
   */
  public onPaymentsSelected(payments: Payment[]): void {
    this.payments = payments;
    this.resetErrorsMessages();

    if (this.isFormValidationActive || this.paymentService.hasExternalPayment(this.payments)) {
      // Run after change detection
      setTimeout(() => this.setupFormValidationListener());
    }
  }

  /**
   * Re-evaluates the submit validation error once the payment form becomes fully valid.
   */
  public onPaymentFormValid(): void {
    if (!this.isFormValidationActive) {
      return;
    }

    this.checkFormValidationError();
  }

  /**
   * Submit the address complete request if not submitted before and submit the upsell if any upsell selected.
   */
  private submitWorkflow(): Observable<any> {
    const addressSubmit$ = this.storageService.addressComplete ? of(void 0) : this.submitAddress();
    const upsellSubmit$ = this.hasUpsellTestSelected() ? this.submitUpsell() : of(void 0);

    return addressSubmit$.pipe(switchMap(() => upsellSubmit$));
  }

  /**
   * Check for any submission or form validation errors.
   */
  private checkErrors(): void {
    this.checkFormValidationError();

    if (this.formValidationError) {
      this.setupFormValidationListener();
      scrollToFirstInvalidControl();
    }

    if (this.paymentService.hasUnsupportedCreditCard(this.payments)) {
      this.submissionErrors = [new InternationalCreditCardNotSupportedError(environment.phone).message];
    }
  }

  /**
   * Returns true when having submission or form validation errors. False otherwise
   */
  private hasErrors(): boolean {
    return Boolean(this.formValidationError || this.submissionErrors.length);
  }

  /**
   * Handle reorder address data response
   *
   * @param {ReorderAddress} reorderData The reorder address data
   */
  private handleReorderAddressResponse(reorderData: ReorderAddress): void {
    if (!reorderData) {
      return;
    }

    this.reorder = reorderData;
    this.fillAddressFormWithReorderData();
  }

  /**
   * Update patient phone in storage
   */
  private readonly updatePatientPhoneInStorageIfNonEmpty = (): void => {
    const phone = this.addressContactInformation.get('phone.number').value;
    if (phone) {
      this.storageService.patient = { ...this.storageService.patient, phone };
    }
  };

  /**
   * Get the upsell tests from the api and then initialize the form with the checkboxes.
   *
   * Recommended tests are not offered on business account orders for sites that skip upsells for them.
   */
  private loadRecommendedTests(): void {
    const testIds = this.storageService.orderedTests.map((test) => test.id);

    if (this.businessAccountService.shouldSkipUpsells() || !testIds.length) {
      this.loadingService.toggleLoader(false);

      return;
    }

    this.orderService.getOrderUpsells(testIds).subscribe({
      next: (upsells) => this.handleSuccessfulGetOrderUpsells(upsells),
      error: () => this.loadingService.toggleLoader(false),
    });
  }

  /**
   * Handles Successful Get Order Upsells by initializing the form with the upsell tests.
   *
   * @param {OrderUpsell[]} upsells Array of upsells
   */
  private handleSuccessfulGetOrderUpsells(upsells: OrderUpsell[]): void {
    this.upsellTests = this.sortUpsellByPrice(upsells);
    this.initUpsellForm();
    this.loadingService.toggleLoader(false);
  }

  /**
   * Returns upsell sorted by price
   *
   * @param {OrderUpsell[]} upsells Array of upsells unsorted
   */
  private sortUpsellByPrice(upsells: OrderUpsell[]): OrderUpsell[] {
    return upsells.sort((a, b) => b.upsell_price - a.upsell_price);
  }

  /**
   * Initializes the address form.
   */
  private initAddressForm(): void {
    if (this.isPhoneOrder) {
      this.formService.addressContactInformation.removeControl('phone');
      this.formService.addressContactInformation.addControl('phone', this.formService.orderPhoneForm());
    }

    this.addressContactInformation = this.formService.addressContactInformation as FormGroup;
  }

  /**
   * Initializes the additional recommended testing form.
   */
  private initUpsellForm(): void {
    this.upsellTestsForm = this.formBuilder.group({ tests: new FormGroup({}) });

    const previouslyOrderedUpsells: number[] = this.reorder?.tests ? this.getPreviouslyOrderedUpsells() : [];

    this.upsellTests.forEach((upsellTest) => {
      const hasBeenPreviouslyOrdered = previouslyOrderedUpsells.includes(upsellTest.id);

      if (hasBeenPreviouslyOrdered) {
        this.upsellSelected = upsellTest;
      }

      (this.upsellTestsForm.controls.tests as FormGroup).addControl(
        upsellTest.slug,
        new FormControl(hasBeenPreviouslyOrdered),
      );
    });
  }

  /**
   * Returns true weather we have upsell selected. False otherwise
   */
  private hasUpsellTestSelected(): boolean {
    return Boolean(this.upsellTests && this.upsellTestsForm && this.upsellSelected);
  }

  /**
   * Initializes the requests needed for the component.
   */
  private initRequests(): void {
    this.loadingService.toggleLoader(true);

    if (!this.storageService.reorderId) {
      this.loadRecommendedTests();

      return;
    }

    this.orderService
      .getReorderAddressData(this.storageService.reorderId.toString(), this.storageService.getCookie('account_token'))
      .subscribe({
        next: (reorderData) => this.handleReorderAddressResponse(reorderData),
        error: () => this.loadRecommendedTests(),
        complete: () => this.loadRecommendedTests(),
      });
  }

  /**
   * Fill address form with reorder data
   */
  private fillAddressFormWithReorderData(): void {
    this.addressContactInformation.patchValue({
      phone: {
        number: this.reorder.phone,
        voicemail_allowed: !!this.reorder.leave_message,
        sms_allowed: this.reorder.send_sms,
      },
      address: {
        streetAddress: this.reorder.address?.customer_address_street,
        city: this.reorder.address?.customer_address_city,
        state: this.reorder.address?.customer_address_state,
        zipcode: this.reorder.address?.customer_address_zip,
      },
    });
  }

  /**
   * Get the upsell test ids that were previously ordered
   */
  private getPreviouslyOrderedUpsells(): number[] {
    return this.reorder.tests
      .filter(
        (test) =>
          test.customer_tests_type === CustomerTestsTypes.UpsellIndividual ||
          test.customer_tests_type === CustomerTestsTypes.UpsellPanel,
      )
      .map((test) => test.test_id);
  }

  /**
   * Return the Legacy Upsell Store Request
   */
  private getLegacyUpsellStoreRequest(): LegacyUpsellStoreRequest {
    const upsell = new Upsell(this.upsellSelected.name);
    const payments = [
      this.paymentService.getPrimaryPayment(this.payments),
      this.paymentService.getGiftCardAsSecondaryPayment(this.payments),
    ];
    const defaultPaymentType = this.paymentCardFormComponent?.isPayLater
      ? LegacyPaymentTypes.PayLater
      : this.storageService.paymentType;

    return this.payments.length
      ? LegacyUpsellStoreRequest.fromPayments(upsell, payments, this.storageService.patient.name)
      : LegacyUpsellStoreRequest.useDefaultPayment(upsell, defaultPaymentType);
  }

  /**
   * Sets up a listener to track form value changes and update the submit validation error message.
   */
  private setupFormValidationListener(): void {
    this.isFormValidationActive = true;
    this.resetFormValidationSubscription();
    this.addressContactInformation.markAllAsTouched();
    this.checkFormValidationError();
    this.formValidationSubscription = this.addressContactInformation.valueChanges.subscribe(() =>
      this.checkFormValidationError(),
    );
  }

  /**
   * Resets the form validation subscription.
   *
   * This ensures we always subscribe to the correct form components when payments change.
   */
  private resetFormValidationSubscription(): void {
    this.formValidationSubscription?.unsubscribe();
    this.formValidationSubscription = null;
  }

  /**
   * Return the observable of address submit. Once success submitted we disable the address form.
   */
  private submitAddress(): Observable<any> {
    if (this.addressContactInformation.get('phone.sms_allowed').value) {
      this.storageService.sendSMS = true;
    }

    return this.orderService.storeAddress(new AddressUpdateRequest(this.addressContactInformation)).pipe(
      tap(() => {
        this.storageService.addressComplete = true;
        this.formService.disableAllControls(this.addressContactInformation);
      }),
      catchError((error) => {
        this.submissionErrors = this.errorHandlerService.handleResponseError(error);

        return error;
      }),
    );
  }

  /**
   * Place an upsell order when there is any selected.
   */
  private submitUpsell(): Observable<any> {
    const legacyUpsellStore = this.getLegacyUpsellStoreRequest();

    return this.orderService.legacyStoreUpsell(legacyUpsellStore).pipe(
      tap((response: LegacyUpsellStoreResponse) => this.handleSuccessfulStoreUpsell(response)),
      catchError((error) => {
        // Swallow the first submission error because the form does not exist yet.
        if (this.shouldDisplayPaymentForm) {
          this.submissionErrors = this.errorHandlerService.handleResponseError(error);
        }

        this.upsellPaymentFailed = true;

        return error;
      }),
    );
  }

  /**
   * Reset errors messages
   */
  private resetErrorsMessages(): void {
    this.submissionErrors = [];
    this.formValidationError = null;
  }
}
